/**
 * Тема кабинета.
 *
 * Переключается АТРИБУТОМ data-theme на корне документа, а не медиазапросом. Причина
 * в ТЗ: мастеру тёмная тема нужна в зале с приглушённым светом независимо от настройки
 * телефона, а администратору за стойкой — светлая при системной тёмной. Медиазапрос
 * описывает устройство, а человек выбирает сам.
 *
 * Системная настройка участвует ровно в одном режиме — «как в системе»: он разрешается
 * в атрибут здесь и в маленьком скрипте в index.html, который ставит атрибут ДО первой
 * отрисовки. Без него мастер, работающий ночью, получал бы вспышку светлого экрана.
 */

export type ThemeMode = "light" | "dark" | "system"
export type ResolvedTheme = "light" | "dark"

/** Ключ обязан совпадать со скриптом в index.html — иначе тема мигает при загрузке. */
const STORAGE_KEY = "plnr.cabinet.theme"

export interface ThemeSnapshot {
  readonly mode: ThemeMode
  readonly resolved: ResolvedTheme
}

const listeners = new Set<() => void>()

function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === "light" || raw === "dark" || raw === "system") return raw
  } catch {
    // Приватное окно или запрет на хранение: тема просто не запомнится между визитами.
  }
  return "system"
}

function systemQuery(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null
  return window.matchMedia("(prefers-color-scheme: dark)")
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode !== "system") return mode
  return systemQuery()?.matches === true ? "dark" : "light"
}

let mode: ThemeMode = readStoredMode()
// Снимок кешируется целиком: useSyncExternalStore обязан получать одну и ту же ссылку,
// пока ничего не изменилось, иначе React уйдёт в бесконечную перерисовку.
let snapshot: ThemeSnapshot = { mode, resolved: resolve(mode) }

function applyToDocument(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return
  document.documentElement.setAttribute("data-theme", resolved)
}

function refresh(): void {
  const resolved = resolve(mode)
  if (snapshot.mode === mode && snapshot.resolved === resolved) return
  snapshot = { mode, resolved }
  applyToDocument(resolved)
  for (const listener of listeners) listener()
}

// Смена системной темы на ходу видна только в режиме «как в системе».
systemQuery()?.addEventListener("change", () => {
  if (mode === "system") refresh()
})

export const themeStore = {
  getSnapshot(): ThemeSnapshot {
    return snapshot
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  setMode(next: ThemeMode): void {
    mode = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Не запомнили — не беда: выбор действует до конца сессии.
    }
    refresh()
  },
}

/** Ставит атрибут при старте: скрипт в index.html мог не отработать (например, в тестах). */
export function syncThemeAttribute(): void {
  applyToDocument(snapshot.resolved)
}
